/**
 * Structured Block Renderer Registry
 *
 * Maps XML tag names to renderer functions for inline formatted content
 * within streaming chat messages. Each renderer receives the inner content
 * of the XML tag and returns a DOM element to insert into the message flow.
 *
 * To add a new structured block type:
 * 1. Add a renderer function that parses the inner XML and returns a DOM element
 * 2. Register it in the registry Map below
 * 3. Update the LLM system prompt to document the new tag format
 *
 * @module structuredBlockRenderers
 */

import { eventBus } from '../core/utils.js';
import {
  ADVICE_TAKEAWAY_CARD_ATTR,
  clearAllAdviceTerminusPills
} from '../core/adviceTerminusPills.js';
import { renderMarkdown } from '../utils/markdownRenderer.js';
import { buildHelpHintPanel } from './helpHintPanel.js';
import { renderPracticeReferencePanel } from './practiceReferencePanel.js';
import {
  buildPredictionBlock, VERDICT_BANDS, VERDICT_BAND_FALLBACK
} from '../components/predictionBlock.js';
import { deriveWindowOptions, formatWindowOption, isValidWindowInterval } from './practiceWindowOptions.js';
import { renderDisclosure, escapeHtml, wireDisclosures } from '../components/disclosure.js';
import { createAssessmentAnswersOnToggle } from './assessmentAnswersLoader.js';
import { renderTopicUnderstanding, renderBrainReveal, understandingCardHoldMs } from '../components/brain/understandingCard.js';
import { renderGuideAnnouncement, flattenGuideAnnouncement } from '../components/guideAnnouncementCard.js';
import { renderSharingInvitationCard, flattenSharingInvitationCard } from '../components/sharingInvitationCard.js';

const HELP_HINT_LABELS = {
  practices: 'What are experiments?',
  insights: 'What are insights?',
  notes: 'What are partner notes?'
};

// Static, author-controlled markup — assigned via innerHTML on an element that
// never carries model-derived content.
const HELP_HINT_ICON =
  '<svg class="structured-card__help-hint-icon" width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
  '<circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.1"/>' +
  '<path d="M8 7.4v3.8" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>' +
  '<circle cx="8" cy="5" r="0.8" fill="currentColor"/>' +
  '</svg>';

/**
 * Attach the "What are X?" help affordance to an inline card, gated by
 * HelpHintService visibility. Clicking opens the right panel with the help
 * content and dismisses the hint for future cards of that type; the link stays
 * on this card so the panel can be reopened.
 *
 * The link is rendered as a SIBLING below the card, inside a wrapper, rather
 * than as card content — the insight card is itself click-to-open, so a nested
 * link would compete with the card's own target, and meta-help sitting inside
 * the card's surface reads as card content.
 *
 * @param {HTMLElement} cardEl
 * @param {'practices'|'insights'} type
 * @param {{interactive?: boolean}} [options] - When interactive is false (static
 *   transcript replay), the hint is an interactive affordance and is omitted.
 * @returns {HTMLElement} The card itself when no hint is due, otherwise a
 *   wrapper holding the card + hint link. Callers must return this node.
 */
function withHelpHint(cardEl, type, { interactive = true } = {}) {
  if (!interactive) return cardEl;
  const svc = window.helpHintService;
  if (!svc || !svc.shouldShow(type)) return cardEl;

  const group = document.createElement('div');
  group.className = 'structured-card-group';
  group.appendChild(cardEl);

  const link = document.createElement('a');
  link.className = 'structured-card__help-hint';
  link.href = '#';
  link.dataset.helpHintType = type;
  link.innerHTML = HELP_HINT_ICON;
  // Label is its own element so the hover underline lands on the text only,
  // not across the icon and its gap.
  const label = document.createElement('span');
  label.className = 'structured-card__help-hint-label';
  label.textContent = HELP_HINT_LABELS[type] || 'Learn more';
  link.appendChild(label);
  link.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const panel = buildHelpHintPanel(type);
    if (!panel) return;
    eventBus.emit('right-panel:open', {
      title: panel.title,
      content: panel.content,
      contentType: 'help-hint'
    });
    svc.markDismissed(type);
  });
  group.appendChild(link);

  svc.recordExposure(type);
  return group;
}

// Backend composers escape `&`, `<`, `>` when emitting structured-block XML
// (see intakeTailComposer.js escapeXml). Our regex-based parsers extract raw
// inner text, so callers must decode entities before handing strings to
// textContent — otherwise users see literal "&amp;" etc.
function decodeXmlEntities(s) {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Parse <item> elements from inner XML content
 * Items may contain plain text or structured <header>/<subtitle> elements.
 * A structured item may also carry an optional <source> tag (`intake` on the
 * one <advice-topics> row that restates the concern the user named at intake);
 * it is provenance only, never rendered as text.
 * @param {string} content - Inner XML string
 * @returns {Array<{text: string}|{header: string, subtitle: string, source: string|undefined}>} Parsed items
 */
function parseItems(content) {
  const items = [];
  const itemPattern = /<item>([\s\S]*?)<\/item>/g;
  let match;
  while ((match = itemPattern.exec(content)) !== null) {
    const inner = match[1].trim();
    if (!inner) continue;

    const header = parseElement(inner, 'header');
    const subtitle = parseElement(inner, 'subtitle');
    const source = parseElement(inner, 'source') || undefined;

    if (header && subtitle) {
      items.push({ header, subtitle, source });
    } else {
      items.push({ text: decodeXmlEntities(inner) });
    }
  }
  return items;
}

/**
 * Build the shared header/subtitle-or-text item list used by structured cards.
 * Model-derived strings are assigned with textContent only (never innerHTML).
 *
 * `leadEyebrow` is opt-in so the eight callers of this builder are unaffected:
 * only <advice-topics> asks for it, and only the item tagged
 * `source === 'intake'` gets it. Textual only — no listeners, no behavior.
 *
 * @param {Array<{text: string}|{header: string, subtitle: string, source: string|undefined}>} items
 * @param {Object} [options]
 * @param {string|null} [options.leadEyebrow] - Eyebrow label rendered above the
 *   `intake`-sourced item, or null/omitted for no eyebrow on any item.
 * @returns {HTMLUListElement}
 */
function buildItemsList(items, { leadEyebrow } = {}) {
  const list = document.createElement('ul');
  list.className = 'structured-card__items';

  for (const item of items) {
    const li = document.createElement('li');
    li.className = 'structured-card__item';

    if (leadEyebrow && item.source === 'intake') {
      li.classList.add('structured-card__item--lead');
      const eyebrowEl = document.createElement('div');
      eyebrowEl.className = 'structured-card__item-eyebrow';
      eyebrowEl.textContent = leadEyebrow;
      li.appendChild(eyebrowEl);
    }

    if (item.header) {
      const headerEl = document.createElement('div');
      headerEl.className = 'structured-card__item-header';
      headerEl.textContent = item.header;
      li.appendChild(headerEl);

      const subtitleEl = document.createElement('div');
      subtitleEl.className = 'structured-card__item-subtitle';
      subtitleEl.textContent = item.subtitle;
      li.appendChild(subtitleEl);
    } else {
      li.textContent = item.text;
    }

    list.appendChild(li);
  }

  return list;
}

/**
 * Build the shared, behavior-free artifact card scaffold. Renderers remain
 * responsible for roles, listeners, and other interactive behavior.
 * Model-derived strings are assigned with textContent only (never innerHTML).
 *
 * @param {Object} params
 * @param {string} params.cardClass
 * @param {string} params.iconHref
 * @param {string} params.title
 * @param {string|null} [params.description]
 * @param {string|null} [params.subtitle]
 * @param {boolean} [params.showChevron=false]
 * @returns {HTMLElement}
 */
function buildArtifactCard({
  cardClass,
  iconHref,
  title,
  description = null,
  subtitle = null,
  showChevron = false
}) {
  const card = document.createElement('div');
  card.className = cardClass;

  const inner = document.createElement('div');
  inner.className = 'artifact__inner';

  const left = document.createElement('div');
  left.className = 'artifact__left';

  const iconWrapper = document.createElement('div');
  iconWrapper.className = 'artifact__icon-wrapper';
  const icon = document.createElement('div');
  icon.className = 'artifact__icon';
  icon.innerHTML = `<svg width="25" height="28" aria-hidden="true"><use href="${iconHref}"/></svg>`;
  iconWrapper.appendChild(icon);
  left.appendChild(iconWrapper);

  const content = document.createElement('div');
  content.className = 'artifact__content';

  const titleRow = document.createElement('div');
  titleRow.className = 'artifact__title-row';
  const titleEl = document.createElement('span');
  titleEl.className = 'artifact__title';
  titleEl.textContent = title;
  titleRow.appendChild(titleEl);
  content.appendChild(titleRow);

  if (description) {
    const descEl = document.createElement('div');
    descEl.className = 'artifact__description';
    descEl.textContent = description;
    content.appendChild(descEl);
  }

  if (subtitle) {
    const subtitleEl = document.createElement('div');
    subtitleEl.className = 'artifact__subtitle';
    subtitleEl.textContent = subtitle;
    content.appendChild(subtitleEl);
  }

  left.appendChild(content);
  inner.appendChild(left);

  if (showChevron) {
    const actions = document.createElement('div');
    actions.className = 'artifact__actions artifact__actions--chevron-only';
    actions.innerHTML = `
      <svg class="artifact__chevron" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    inner.appendChild(actions);
  }

  card.appendChild(inner);
  return card;
}

/**
 * Parse a single named element's text content from inner XML
 * @param {string} content - Inner XML string
 * @param {string} tagName - Element name to find
 * @returns {string|null} Text content or null
 */
function parseElement(content, tagName) {
  const pattern = new RegExp(`<${tagName}>([\\s\\S]*?)</${tagName}>`);
  const match = content.match(pattern);
  return match ? decodeXmlEntities(match[1].trim()) : null;
}

/**
 * Parse all occurrences of a named element from inner XML.
 * Used for tags that may appear multiple times (e.g. <secondary> for
 * composite intermediary cards with multiple secondary trait pills).
 *
 * @param {string} content - Inner XML string
 * @param {string} tagName - Element name to find
 * @returns {string[]} Array of trimmed text contents (empty if none)
 */
function parseAllElements(content, tagName) {
  const pattern = new RegExp(`<${tagName}>([\\s\\S]*?)</${tagName}>`, 'g');
  const results = [];
  let m;
  while ((m = pattern.exec(content)) !== null) {
    results.push(decodeXmlEntities(m[1].trim()));
  }
  return results;
}

/**
 * Parse the <detail> element with optional base64 decoding.
 *
 * Supports two forms:
 *   <detail>raw content</detail>                       (legacy / orientation)
 *   <detail encoding="base64">BASE64STRING</detail>    (deterministic composers)
 *
 * The base64 form immunizes JSON payloads against any character that could
 * collide with </detail> or other XML control sequences.
 *
 * @param {string} content - Inner XML of an <insight> block
 * @returns {string|null} Decoded detail content or null
 */
function parseDetailElement(content) {
  const attrMatch = content.match(/<detail(\s+encoding="([^"]+)")?>([\s\S]*?)<\/detail>/);
  if (!attrMatch) return null;
  const encoding = attrMatch[2] || null;
  const raw = attrMatch[3].trim();
  if (encoding === 'base64') {
    try {
      // atob → URI-decoded UTF-8 round-trip (mirror of the encoder)
      return decodeURIComponent(escape(atob(raw)));
    } catch (err) {
      console.error('[structuredBlockRenderers] Failed to decode base64 <detail>:', err);
      return null;
    }
  }
  return raw;
}

/**
 * Render an <l2-closing-summary> block — emitted by L2 discovery closings to
 * summarize the conversation's takeaways. Consumed by the L2 validation loop
 * (see ModeOrchestrator._extractL2ClosingSummary and ActionRouter end_discovery).
 *
 * Supports two item formats:
 *   Plain text:   <item>Observation text</item>
 *   Structured:   <item><header>Bold headline</header><subtitle>Supporting detail</subtitle></item>
 *
 * Optional element:
 *   <title>Custom Title</title>  (defaults to "Key Takeaways")
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element
 */
function renderL2ClosingSummary(innerContent) {
  const items = parseItems(innerContent);
  if (items.length === 0) return null;

  const title = parseElement(innerContent, 'title') || 'Key Takeaways';
  const card = document.createElement('div');
  card.className = 'structured-card structured-card--takeaways';

  // Label
  const label = document.createElement('div');
  label.className = 'structured-card__label';
  label.textContent = title;
  card.appendChild(label);

  card.appendChild(buildItemsList(items));

  return card;
}

/**
 * Render a <reflection> block — a softer, single-point callout
 *
 * Expected XML format:
 *   <reflection>
 *   <text>The core reflection or observation</text>
 *   </reflection>
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element
 */
function renderReflection(innerContent) {
  const text = parseElement(innerContent, 'text') || innerContent.trim();
  if (!text) return null;

  const card = document.createElement('div');
  card.className = 'structured-card structured-card--reflection';

  const label = document.createElement('div');
  label.className = 'structured-card__label';
  label.textContent = 'Reflection';
  card.appendChild(label);

  const body = document.createElement('p');
  body.className = 'structured-card__text';
  body.textContent = text;
  card.appendChild(body);

  return card;
}

/**
 * Render an <insight> block — a clinical insight card (programmatic only)
 *
 * Expected XML format:
 *   <insight>
 *   <label>Affection Patterns</label>
 *   <title>Acts of Care</title>
 *   <secondary>Focused Attention</secondary>
 *   <item><header>Finding headline</header><subtitle>Supporting detail</subtitle></item>
 *   <item><header>Another finding</header><subtitle>More detail</subtitle></item>
 *   <detail>JSON or markdown content for side panel (optional)</detail>
 *   </insight>
 *
 * The <title> and <subtitle> elements are optional. When present, they display
 * the primary result prominently below the label. Only used by programmatic paths
 * (OrientationMode) — LLM-generated L2 closings use <l2-closing-summary> instead.
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element
 */
function renderInsight(innerContent, { interactive = true } = {}) {
  const items = parseItems(innerContent);
  if (items.length === 0) return null;

  const label = parseElement(innerContent, 'label') || 'Insight';
  const title = parseElement(innerContent, 'title');
  // Multiple <secondary> tags are supported for composite intermediary cards
  // (e.g. BFI-20 picks 1 primary trait + up to 2 secondaries). Single-tag
  // and zero-tag cases produce arrays of length 1 and 0 respectively.
  const secondaries = parseAllElements(innerContent, 'secondary');

  // In static replay (interactive: false) the detail drill-in is omitted, so the
  // card renders as the plain, non-clickable variant with no chevron/listener.
  const hasDetail = !!parseDetailElement(innerContent) && interactive;

  const card = document.createElement('div');
  card.className = hasDetail
    ? 'structured-card structured-card--insight structured-card--clickable'
    : 'structured-card structured-card--insight';

  // Label row (label + optional "View full results" chevron)
  const labelRow = document.createElement('div');
  labelRow.className = 'structured-card__label-row';

  const labelEl = document.createElement('div');
  labelEl.className = 'structured-card__label';
  labelEl.textContent = label;
  labelRow.appendChild(labelEl);

  if (hasDetail) {
    const learnMore = document.createElement('span');
    learnMore.className = 'structured-card__learn-more';

    learnMore.innerHTML = `
      <span class="structured-card__learn-more-text">View full results</span>
      <svg class="structured-card__learn-more-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5"
          stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

    labelRow.appendChild(learnMore);
  }

  card.appendChild(labelRow);

  // Result pills (primary + optional secondary)
  if (title) {
    card.classList.add('structured-card--has-pills');

    const pillsRow = document.createElement('div');
    pillsRow.className = 'structured-card__pills';

    const primaryPill = document.createElement('span');
    primaryPill.className = 'structured-card__pill structured-card__pill--primary';
    // LLM-derived text → textContent only (never innerHTML). Only show the
    // "(Primary)" label when there's also a secondary pill for contrast.
    primaryPill.appendChild(document.createTextNode(title));
    if (secondaries.length > 0) {
      primaryPill.appendChild(document.createTextNode(' '));
      const primaryLabel = document.createElement('span');
      primaryLabel.className = 'structured-card__pill-label';
      primaryLabel.textContent = '(Primary)';
      primaryPill.appendChild(primaryLabel);
    }
    pillsRow.appendChild(primaryPill);

    for (const secondaryText of secondaries) {
      const secondaryPill = document.createElement('span');
      secondaryPill.className = 'structured-card__pill structured-card__pill--secondary';
      // LLM-derived text → textContent only (never innerHTML).
      secondaryPill.appendChild(document.createTextNode(secondaryText));
      secondaryPill.appendChild(document.createTextNode(' '));
      const secondaryLabel = document.createElement('span');
      secondaryLabel.className = 'structured-card__pill-label';
      secondaryLabel.textContent = '(Secondary)';
      secondaryPill.appendChild(secondaryLabel);
      pillsRow.appendChild(secondaryPill);
    }

    card.appendChild(pillsRow);
  }

  card.appendChild(buildItemsList(items));

  // Click-to-open detail panel — supports both raw and base64-encoded
  // <detail> payloads. Base64 form is used by deterministic clinical
  // intermediary composers so JSON content can never collide with </detail>.
  const detailContent = parseDetailElement(innerContent);
  if (detailContent && interactive) {
    const openDetailPanel = () => {
      const container = buildDetailPanel(detailContent);

      eventBus.emit('right-panel:open', {
        // No chrome title — the in-panel title-block carries the framework name.
        title: '',
        content: container,
        contentType: 'insight'
      });
    };

    card.addEventListener('click', openDetailPanel);
  }

  return withHelpHint(card, 'insights', { interactive });
}

/**
 * The right-panel content wrapper every assessment-detail host shares: the
 * structured panel when there is one, the persisted prose when there is not,
 * inside the `topic-detail` container.
 *
 * The wrapper is not decoration. `.topic-detail` declares
 * `container: topic-detail / inline-size` (discovery.css), which is what the
 * plate's narrow-panel padding and the discovery container queries key on —
 * a host that omits it silently keeps desktop spacing at every panel width.
 * Centralised here so no host can forget it; the three hosts previously
 * built this wrapper themselves and the chat one did not build it at all.
 *
 * @param {{ detail?: Object|null, markdown?: string }} [opts] - `detail` is
 *   composed structured data; when absent, `markdown` is rendered instead.
 * @returns {HTMLElement} Panel content element
 */
export function buildAssessmentDetailContent({ detail = null, markdown = '' } = {}) {
  const content = document.createElement('div');
  content.className = 'topic-detail insight-detail-wrap';

  if (detail) {
    content.appendChild(buildStructuredDetailPanel(detail));
  } else {
    const md = document.createElement('div');
    md.className = 'insights-detail__markdown';
    md.innerHTML = renderMarkdown(markdown);
    content.appendChild(md);
  }

  return content;
}

/**
 * Build detail panel content for the in-chat insight card. Detects structured
 * JSON (from compose.js) and builds rich DOM with cards, badges, and progress
 * bars; falls back to markdown rendering for plain text content. Both branches
 * go through `buildAssessmentDetailContent`, so this host renders identically
 * to the two /library hosts.
 *
 * @param {string} detailContent - JSON string or markdown text
 * @returns {HTMLElement} Panel content element
 */
export function buildDetailPanel(detailContent) {
  let data = null;
  try {
    data = JSON.parse(detailContent);
  } catch {
    // Not JSON — the payload is markdown, handled below.
  }

  const detail = data?.format === 'structured' ? data : null;
  return buildAssessmentDetailContent({
    detail,
    markdown: detail ? '' : detailContent
  });
}

/**
 * Build a rich structured detail panel from composed data. DOM order:
 * header → "Overview" disclosure (expanded; its body is the framework intro
 * followed by one emphasis plate per `data.sections[]`, unbounded) → "Your
 * Full Results" disclosure (expanded, only when rankings exist) → "Your
 * answers" disclosure (collapsed, hydrated lazily on first expand; only when
 * the assessment has answered questions to show). With no `data.intro` there
 * is no Overview disclosure, and the plates lead the panel. All four hosts (chat "View full results" card, /library Assessments
 * tab, /library Topics-tab clinical-insight drill, and this function's own
 * self-call) share this exact structure — see
 * project-docs/plans/library-assessment-detail-redesign.md.
 *
 * `data.answeredCount`/`data.questionnaireId` are runtime-enriched fields
 * stamped by the backend (never authored, never persisted) — legacy/persisted
 * payloads that lack them simply omit the plates' count-link and the whole
 * "Your answers" disclosure; that is the compatibility story, not a bug.
 * `data.completedAt` is a plain passthrough field — only the /library
 * Assessments tab host currently supplies it (from its
 * `getFrameworkResultsForUser` row, not the composed detail payload) — it
 * only ever renders as the "Completed <date>" line at the top of "Your
 * answers", so other hosts simply omit that line.
 *
 * @param {Object} data - Structured data from compose.js
 * @param {string} [data.intro] - Framework introduction markdown
 * @param {string} [data.introHeading] - Overview disclosure label (defaults below)
 * @param {Array} data.sections - Pattern sections (primary/secondary/balanced)
 * @param {Array} [data.rankings] - Full profile rankings
 * @param {number} [data.answeredCount] - Answered-question count (runtime-enriched)
 * @param {string} [data.questionnaireId] - Content-registry questionnaire id (runtime-enriched)
 * @param {string} [data.completedAt] - ISO completion date, shown atop "Your answers"
 * @param {{ onToggle?: (key: string, expanded: boolean) => void }} [options] -
 *   `onToggle` is COMPOSED alongside this function's own default "Your
 *   answers" lazy-hydration handler (js/chat/assessmentAnswersLoader.js) —
 *   both fire on every toggle, neither replaces the other. Hosts need this
 *   only for behavior beyond hydration (hydration itself is automatic on
 *   all four hosts with zero config).
 * @returns {HTMLElement} Panel content element
 */
export function buildStructuredDetailPanel(data, { onToggle } = {}) {
  const panel = document.createElement('div');
  panel.className = 'insight-detail';

  // Title-block header (framework identity) — mirrors the "About this pattern"
  // page. Rendered only when the framework name is present (stamped onto the
  // detail by resolveComposedTemplates). The tagline sub-line is optional.
  if (data.frameworkName) {
    const header = document.createElement('div');
    header.className = 'insight-detail__header';

    const heading = document.createElement('h2');
    heading.className = 'insight-detail__heading';
    heading.textContent = data.frameworkName;
    header.appendChild(heading);

    if (data.tagline) {
      const tagline = document.createElement('p');
      tagline.className = 'sd-subtitle insight-detail__tagline';
      tagline.textContent = data.tagline;
      header.appendChild(tagline);
    }

    panel.appendChild(header);
  }

  // Whether the assessment has answered questions to show — gates the
  // "Your answers" disclosure below, off the two runtime-enriched fields
  // (Phase 3). No per-plate jump-link: the disclosure is reachable by scroll.
  const hasAnswers = data.answeredCount > 0 && !!data.questionnaireId;

  // Result plates — one per data.sections[] entry, unbounded (YSQ-S3 emits up
  // to 3). Eyebrow text + order carry the hierarchy; no cap here.
  const plates = (data.sections || []).map(section => buildResultPlate(section));

  // Overview — shared gray disclosure, default-expanded, and the FIRST zone on
  // the page: the framework explainer opens, and the result plates sit inside
  // that same disclosure directly beneath its prose (owner decision), so
  // collapsing Overview folds the explainer and the results away together.
  // Absent an intro there is no disclosure to nest in, and the plates lead the
  // panel on their own.
  if (data.intro) {
    const introBodyHtml = `<div class="insight-detail__intro-body">${renderMarkdown(data.intro)}</div>`;
    const overviewZone = appendZone(panel, renderDisclosure(
      'assessment-overview',
      data.introHeading || 'About this assessment',
      introBodyHtml,
      { expanded: true }
    ));
    // `appendDisclosureHtml` leaves the disclosure body as the zone's last
    // element (button + body, in that order).
    const overviewBody = overviewZone.lastElementChild;
    for (const plate of plates) overviewBody.appendChild(plate);
  } else {
    for (const plate of plates) panel.appendChild(plate);
  }

  // Your Full Results — standalone disclosure (not inside a plate): plate is
  // the narrative register, bars are instrument data. Default-expanded, only
  // rendered when rankings exist (matches prior behavior).
  if (data.rankings && data.rankings.length > 0) {
    appendZone(panel, renderDisclosure(
      'assessment-results',
      'Your full results',
      buildRankingsBodyHtml(data.rankings),
      { expanded: true }
    ));
  }

  // Your answers — collapsed by default, hydrated lazily on first expand
  // (see assessmentAnswersLoader's onToggle below); the count chip shows
  // only while collapsed. Wrapped in its own programmatically-focusable zone
  // (tabindex="-1") so the plates' count-link jump lands on THIS section,
  // not the whole (much taller) panel — same pattern as TopicDetailView's
  // `[data-sections-anchor]`. A "Completed <date>" line (when data.completedAt
  // is present) precedes the answers themselves, outside the
  // `.insight-detail__answers-content` zone the loader overwrites — so
  // hydration/error/retry never clobbers it.
  if (hasAnswers) {
    let completedHtml = '';
    if (data.completedAt) {
      const completed = new Date(data.completedAt);
      if (!Number.isNaN(completed.getTime())) {
        const label = completed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        completedHtml = `<p class="insight-detail__answers-completed">Completed ${escapeHtml(label)}</p>`;
      }
    }
    const placeholderHtml = `${completedHtml}<div class="insight-detail__answers-content"><p class="insight-detail__answers-placeholder">Loading your answers…</p></div>`;
    const answersZone = document.createElement('section');
    answersZone.className = 'insight-detail__answers-zone';
    answersZone.tabIndex = -1;
    appendZone(panel, renderDisclosure(
      'assessment-answers',
      'Your answers',
      placeholderHtml,
      { expanded: false, count: data.answeredCount }
    ), answersZone);
  }

  // "Your answers" hydration fires on first expand (idle -> loading in the
  // loader's state machine), with error-retry on collapse/re-expand; zero
  // per-host wiring (Phase 5). A caller-supplied `onToggle` still fires on
  // every toggle: composed alongside this one, never replaced, so a future
  // host can add its own toggle behavior (e.g. analytics) without having to
  // reimplement hydration.
  const answersOnToggle = hasAnswers
    ? createAssessmentAnswersOnToggle(panel, data.questionnaireId)
    : null;
  const combinedOnToggle = (answersOnToggle || onToggle)
    ? (key, expanded) => {
      if (answersOnToggle) answersOnToggle(key, expanded);
      if (onToggle) onToggle(key, expanded);
    }
    : undefined;

  // Wired once, here, so every host (chat card, both /library drills, and
  // this function's own self-call) gets delegation for free — none can
  // forget it.
  wireDisclosures(panel, { onToggle: combinedOnToggle });

  return panel;
}

/**
 * Insert a `renderDisclosure` HTML string (button + sibling body) into a DOM
 * container via a `<template>` — the fragment holds both top-level nodes, so
 * a single `appendChild` moves them in document order without re-parsing
 * `container`'s existing children. `html` is TRUSTED markup (composed by
 * this module from `renderMarkdown`/`escapeHtml`'d fields) — never raw
 * model/user text.
 *
 * @param {HTMLElement} container
 * @param {string} html - Output of `renderDisclosure`
 */
function appendDisclosureHtml(container, html) {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  container.appendChild(template.content);
}

/**
 * Append one disclosure to the panel inside its own zone wrapper — the DOM
 * twin of `zoneWrapHtml` (components/disclosure.js), which this panel cannot
 * use because it composes elements rather than one HTML string.
 *
 * The wrapper carries both zone markers: `.sd-zone-wrap` for the closing-rule
 * grammar and `data-sd-zone` for the pinning. This panel earns the pin — three
 * zones, of which "Your answers" expands to a whole questionnaire — and every
 * one of its hosts opens it in the right panel, which is the `--bg-000`
 * scroller the pinned header's paint-out strip assumes.
 *
 * @param {HTMLElement} panel
 * @param {string} html - Output of `renderDisclosure`
 * @param {HTMLElement} [zone] - A caller-built wrapper to use instead of a
 *   plain div (the answers zone is a focusable <section>)
 * @returns {HTMLElement} The wrapper, so a caller can go on filling its body
 */
function appendZone(panel, html, zone = document.createElement('div')) {
  zone.classList.add('sd-zone-wrap');
  zone.setAttribute('data-sd-zone', '');
  appendDisclosureHtml(zone, html);
  panel.appendChild(zone);
  return zone;
}

/**
 * Build one result plate (primary/secondary/balanced — visually identical;
 * eyebrow text + order carry the hierarchy). Shared `.sd-takeaway` emphasis
 * surface, per the locked plate-led-layout decision.
 *
 * Anatomy, top to bottom: eyebrow (`section.tag`) → band headline
 * (`section.title`) → optional serif lede (`section.lede`) →
 * markdown body (`section.content`) → optional gentle note.
 *
 * @param {Object} section - Section data ({ type, tag, title, lede?, content, gentleNote? })
 * @returns {HTMLElement} Plate element
 */
function buildResultPlate(section) {
  const plate = document.createElement('div');
  plate.className = 'sd-takeaway sd-takeaway--plate insight-detail__plate';

  const label = document.createElement('div');
  label.className = 'sd-takeaway__label';
  label.textContent = section.tag || '';
  plate.appendChild(label);

  const band = document.createElement('h3');
  band.className = 'insight-detail__plate-band';
  band.textContent = section.title;
  plate.appendChild(band);

  if (section.lede) {
    const lede = document.createElement('p');
    lede.className = 'sd-plate-lede';
    lede.textContent = section.lede;
    plate.appendChild(lede);
  }

  // Content
  const content = document.createElement('div');
  content.className = 'insight-detail__card-content';
  content.innerHTML = renderMarkdown(section.content);
  plate.appendChild(content);

  // Gentle note — inline callout, not a section header; stays inside the
  // plate with its terracotta label + left-border italic styling.
  if (section.gentleNote) {
    const note = document.createElement('div');
    note.className = 'insight-detail__gentle-note';

    const noteLabel = document.createElement('div');
    noteLabel.className = 'insight-detail__gentle-note-label';
    noteLabel.textContent = 'A gentle note';
    note.appendChild(noteLabel);

    const noteText = document.createElement('div');
    noteText.className = 'insight-detail__gentle-note-text';
    noteText.innerHTML = renderMarkdown(section.gentleNote);
    note.appendChild(noteText);

    plate.appendChild(note);
  }

  return plate;
}

/**
 * The band word for a ranking row's right-aligned value: the authored `level`
 * with the trait name stripped out of it when present ("Low Extraversion" →
 * "Low" — the name already leads the row, so repeating it is noise). Purely
 * lexical, so it generalizes across frameworks; a level that doesn't contain
 * the name passes through whole.
 *
 * @param {string} level - Authored band label (e.g. "Moderate Agreeableness")
 * @param {string} name - The ranking's dimension name (e.g. "Agreeableness")
 * @returns {string} The band word alone, or the full level
 */
function rankingBandWord(level, name) {
  const lvl = String(level || '');
  const nm = String(name || '');
  if (!nm) return lvl;
  const idx = lvl.toLowerCase().indexOf(nm.toLowerCase());
  if (idx === -1) return lvl;
  const stripped = (lvl.slice(0, idx) + lvl.slice(idx + nm.length)).replace(/\s+/g, ' ').trim();
  return stripped || lvl;
}

/**
 * Build the "Your full results" disclosure body — an HTML string (not DOM
 * nodes), since it's handed to `renderDisclosure`. Authored ranking fields
 * (`name`/`level`) are escaped since they land in a string-built fragment.
 *
 * Row anatomy (unified score row): name left; band word + small raw score
 * right-aligned on the same baseline; a solid single-color bar below. The
 * band word IS the interpretation, so there is no caption line under the bar.
 *
 * Field aliases: most composers emit `{ name, level }`, but DERS-16's
 * persisted intermediaries emit `{ label, severity }` (lowercase severity) —
 * aliased and capitalized here so persisted payloads render fully without a
 * data migration.
 *
 * @param {Array} rankings - Array of { name|label, score, maxScore, level|severity }
 * @returns {string} HTML fragment
 */
function buildRankingsBodyHtml(rankings) {
  const rows = rankings.map(r => {
    const pct = Math.round((r.score / r.maxScore) * 100);
    const name = r.name ?? r.label ?? '';
    const band = rankingBandWord(r.level ?? r.severity ?? '', name);
    const bandDisplay = band ? band.charAt(0).toUpperCase() + band.slice(1) : '';
    return `
      <div class="insight-detail__ranking-row">
        <div class="insight-detail__ranking-header">
          <span class="insight-detail__ranking-name">${escapeHtml(name)}</span>
          <span class="insight-detail__ranking-value">${escapeHtml(bandDisplay)}<span class="insight-detail__ranking-score">${escapeHtml(r.score)}/${escapeHtml(r.maxScore)}</span></span>
        </div>
        <div class="insight-detail__bar-track">
          <div class="insight-detail__bar-fill" style="width: ${pct}%"></div>
        </div>
      </div>
    `;
  }).join('');
  return `<div class="insight-detail__rankings-list">${rows}</div>`;
}

/**
 * Render an <intermediary-observations> block — the LLM-path intermediary card.
 *
 * Expected XML format (produced by Sonnet via buildIntermediaryNarrationPrompt v5):
 *   <intermediary-observations>
 *   <takeaways>
 *   <item><header>...</header><subtitle>...</subtitle></item>
 *   <item><header>...</header><subtitle>...</subtitle></item>
 *   </takeaways>
 *   <exploration>             (optional — omitted when prompt skip rule fires)
 *   <item>Short observational thread</item>
 *   <item>Another thread</item>
 *   </exploration>
 *   </intermediary-observations>
 *
 * Renders as a single bordered card with two sections; both section headers
 * are hardcoded here (not controlled by the LLM). The <exploration> section
 * is rendered only when present and non-empty.
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element
 */
function renderIntermediaryObservations(innerContent) {
  const takeawaysMatch = innerContent.match(/<takeaways>([\s\S]*?)<\/takeaways>/);
  const takeawayItems = takeawaysMatch ? parseItems(takeawaysMatch[1]) : [];
  if (takeawayItems.length === 0) return null;

  const explorationMatch = innerContent.match(/<exploration>([\s\S]*?)<\/exploration>/);
  const explorationItems = explorationMatch ? parseItems(explorationMatch[1]) : [];

  const card = document.createElement('div');
  card.className = 'structured-card structured-card--observations';

  // Section 1: Takeaways — hardcoded label
  const takeawaysLabel = document.createElement('div');
  takeawaysLabel.className = 'structured-card__label';
  takeawaysLabel.textContent = "What I'm Noticing";
  card.appendChild(takeawaysLabel);

  card.appendChild(buildItemsList(takeawayItems));

  // Section 2 (optional): Exploration — only render when non-empty
  if (explorationItems.length > 0) {
    const explorationLabel = document.createElement('div');
    explorationLabel.className = 'structured-card__label structured-card__label--exploration';
    explorationLabel.textContent = "What I'd Still Like to Explore";
    card.appendChild(explorationLabel);

    const explorationList = document.createElement('ul');
    explorationList.className = 'structured-card__exploration';
    for (const item of explorationItems) {
      const li = document.createElement('li');
      li.className = 'structured-card__exploration-item';
      li.textContent = item.text || item.header || '';
      explorationList.appendChild(li);
    }
    card.appendChild(explorationList);
  }

  return card;
}

/**
 * Render a standalone <exploration> block — clinical-path sibling after <insight>.
 *
 * Expected XML format (emitted by _buildPrebuiltIntermediaryString when a
 * clinical composer returns a non-null `exploration` array):
 *   <exploration>
 *   <item>Short observational thread</item>
 *   <item>Another thread</item>
 *   </exploration>
 *
 * Rendered as a smaller sibling card that visually aligns with the preceding
 * <insight> card. Returns null when no items are present (opt-out path).
 *
 * Only invoked for the top-level sibling block — nested <exploration> inside
 * <intermediary-observations> is parsed by that parent renderer and never
 * reaches this function (the parent's closing tag consumes the inner tag).
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element
 */
function renderExploration(innerContent) {
  const items = parseItems(innerContent);
  if (items.length === 0) return null;

  const card = document.createElement('div');
  card.className = 'structured-card structured-card--exploration-standalone';

  const label = document.createElement('div');
  label.className = 'structured-card__label structured-card__label--exploration';
  label.textContent = "What I'd Still Like to Explore";
  card.appendChild(label);

  const list = document.createElement('ul');
  list.className = 'structured-card__exploration';
  for (const item of items) {
    const li = document.createElement('li');
    li.className = 'structured-card__exploration-item';
    li.textContent = item.text || item.header || '';
    list.appendChild(li);
  }
  card.appendChild(list);

  return card;
}

/**
 * Render a <common-issues> block — the INTAKE opener's recognition menu.
 *
 * Emitted by the agent between the screening reflection and the locked closing
 * question (see intakePrompt.js → "Paragraph 2"). Purely presentational: the
 * user answers in prose, so the card carries no click target, no data
 * attributes, and no state.
 *
 * Expected XML format:
 *   <common-issues>
 *   <item>The same fight keeps repeating</item>
 *   <item>Not feeling heard</item>
 *   </common-issues>
 *
 * The label is a UI constant rather than wire data — this opener is otherwise
 * locked copy, so the model is not given a lever to rename the card.
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element
 */
function renderCommonIssues(innerContent) {
  const items = parseItems(innerContent);
  if (items.length === 0) return null;

  const card = document.createElement('div');
  card.className = 'structured-card structured-card--common-issues';

  const label = document.createElement('div');
  label.className = 'structured-card__label structured-card__label--common-issues';
  label.textContent = 'Common Issues';
  card.appendChild(label);

  const list = document.createElement('ul');
  list.className = 'structured-card__issues';
  for (const item of items) {
    const li = document.createElement('li');
    li.className = 'structured-card__issue';
    li.textContent = item.text || item.header || '';
    list.appendChild(li);
  }
  card.appendChild(list);

  return card;
}

/**
 * Render an <advice-topics> block — the Foundation → Advice bridge opener's
 * topic menu. Emitted by Sonnet on the bridge turn as exactly 3 synthesized
 * topics: the concern the user named at intake first when one is still
 * unworked, then topics drawn from the Foundation assessment insights. Visual
 * shape mirrors <l2-closing-summary> (title + header/subtitle items) since both
 * are "inline suggestion card" patterns.
 *
 * Each item carries a `<source>` tag (`intake` | `assessment`) recording where
 * the topic came from. The `intake` row — there is at most one, always first —
 * is the only one this card marks, with the "What you came in with" eyebrow.
 * <experiment-directions> shares this builder and emits no `<source>`, so it
 * is unaffected.
 *
 * Expected XML:
 *   <advice-topics>
 *     <title>...</title>    (optional; defaults to "Some topics we could explore")
 *     <item>
 *       <header>...</header>
 *       <subtitle>...</subtitle>
 *       <source>intake</source>   (optional; `intake` | `assessment`)
 *     </item>
 *     ...
 *   </advice-topics>
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element
 */
function renderAdviceTopics(innerContent) {
  return renderSuggestionMenu(innerContent, 'Some topics we could explore', {
    leadEyebrow: 'What you came in with'
  });
}

/**
 * Shared body for the "inline suggestion menu" cards (<advice-topics>,
 * <experiment-directions>): a label plus the parsed item list, on the
 * `.structured-card--advice-topics` class. Both tags differ only in their
 * default title.
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @param {string} defaultTitle - Label used when the block carries no <title>
 * @param {Object} [options]
 * @param {string|null} [options.leadEyebrow] - Passed through to
 *   `buildItemsList`; only <advice-topics> supplies one.
 * @returns {HTMLElement|null} Rendered card element, or null when it has no items
 */
function renderSuggestionMenu(innerContent, defaultTitle, { leadEyebrow = null } = {}) {
  const items = parseItems(innerContent);
  if (items.length === 0) return null;

  const title = parseElement(innerContent, 'title') || defaultTitle;

  const card = document.createElement('div');
  card.className = 'structured-card structured-card--advice-topics';

  const label = document.createElement('div');
  label.className = 'structured-card__label';
  label.textContent = title;
  card.appendChild(label);

  card.appendChild(buildItemsList(items, { leadEyebrow }));

  return card;
}

/**
 * Render an <experiment-directions> block — the display half of the terminus
 * recommendation flow's experiment beat. The agent presents 2–3 pre-selected
 * experiment directions as a card; the DECISION is made on the
 * `advice-experiment-directions` slot group mounted alongside it, whose
 * headers are the resolved labels (model-authored titles, or Option 1/2/3
 * fallbacks) the slot group renders. Same item shape and the same
 * `.structured-card--advice-topics` class as <advice-topics> — deliberately no
 * bespoke card and no new CSS: this is the same "inline suggestion menu"
 * pattern.
 *
 * Expected XML:
 *   <experiment-directions>
 *     <title>...</title>    (optional; defaults to "Experiments we could try")
 *     <item>
 *       <header>Porch evenings</header>
 *       <subtitle>...</subtitle>
 *     </item>
 *     ...
 *   </experiment-directions>
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element, or null when it has no items
 */
function renderExperimentDirections(innerContent) {
  return renderSuggestionMenu(innerContent, 'Experiments we could try');
}

/**
 * Render an <advice-takeaway> block — emitted at the end of Advice sessions in
 * either PATH_WORK voice (Explore/Reflective and Act/Practical) via the
 * `emitAdviceTakeaway` server-side tool. Visual
 * shape mirrors <l2-closing-summary> (title + header/subtitle items) — same
 * `.structured-card--takeaways` CSS class, no v1 visual divergence.
 *
 * The header is a fixed UI constant, not wire data: the composer no longer
 * emits <title>, and any <title> in a historical card's persisted XML (the
 * param was once model-overridable) is deliberately ignored, so replayed
 * cards relabel to "Key Takeaways" too. <l2-closing-summary> keeps its own
 * parsed title — this constant does not leak to it.
 *
 * Expected XML:
 *   <advice-takeaway>
 *     <item>
 *       <header>...</header>
 *       <subtitle>...</subtitle>
 *     </item>
 *     ...
 *   </advice-takeaway>
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element
 */
function renderAdviceTakeaway(innerContent) {
  const items = parseItems(innerContent);
  if (items.length === 0) return null;

  // Live half of terminus retirement (advice-unified-terminus §3.9): when the
  // takeaway card renders, the takeaway is the shared landing — clear EVERY live
  // Advice terminus pill (artifact + correction), not just `key-takeaways`. The
  // durable half (activePills filter-out) lives in the emitAdviceTakeaway
  // executor (retireAllEarlyPills). Cleared on live render AND replay: a stale
  // row whose durable retire was lost is swept by the replayed card.
  clearAllAdviceTerminusPills();

  const title = 'Key Takeaways';

  const card = document.createElement('div');
  card.className = 'structured-card structured-card--takeaways';
  // Marks the landing for the live client (see hasRenderedAdviceTakeaway): a
  // queued "Sum this up" click must not fire after the model landed on its own.
  // Stamped on replay too — the guard has to survive a reload.
  card.setAttribute(ADVICE_TAKEAWAY_CARD_ATTR, 'true');

  const label = document.createElement('div');
  label.className = 'structured-card__label';
  label.textContent = title;
  card.appendChild(label);

  card.appendChild(buildItemsList(items));

  return card;
}

/**
 * Section labels for the check-in takeaway card, in render order. The card
 * header and these sublabels are fixed UI constants — the wire format carries
 * only the item text (and never the mastered|outgrown|inconclusive outcome or
 * the prediction verdict, which are data, not card copy).
 */
const CHECKIN_TAKEAWAY_SECTIONS = [
  ['evidence', 'What we observed'],
  ['next', 'Where this goes next'],
];

/**
 * The card's fixed eyebrow. Deliberately neutral — the card must read honestly
 * whether the user confirms completion or declines and keeps going, so it
 * names what the close produced ("what we learned") and never the outcome
 * ("complete"). It sits above the finding's own name, so the lede carries no
 * inner label of its own.
 * @type {string}
 */
const CHECKIN_TAKEAWAY_HEADER = 'What we learned';

/**
 * Render a `<checkin-takeaway>` block — the practice check-in completion card
 * emitted by the completePractice executor. Two fixed sections ("What we
 * observed" / "Where this goes next") under a lede that names the finding;
 * empty sections are omitted by the composer and skipped here.
 *
 * Expected format:
 *   <checkin-takeaway>
 *     <title>...</title>
 *     <description>...</description>
 *     <evidence><item>...</item></evidence>
 *     <learned><item>...</item></learned>
 *     <lesson>...</lesson>
 *     <next><item><header>...</header><subtitle>...</subtitle></item></next>
 *   </checkin-takeaway>
 *
 * A finding is NAMED when `title` and `description` are BOTH non-blank — they
 * are one unit, never two slots, so a partial emission can never render half a
 * new card. A named finding puts its own name and the sentence stating it on
 * the card ground, with `lesson` as the carry-forward line; `learned` does not
 * render there (the description is its synthesis — and the finding detail page
 * drops it on a named close for the same reason). Not named — pre-change
 * transcripts and partial emissions — renders the legacy lede instead:
 * `lesson` first, then the `learned` lines.
 *
 * The frontier (`next`) is the one section the composer may send structured:
 * a short title over a 1–2 sentence description. Every other section carries
 * plain items, and a frontier written before that shape (or one with no
 * description) arrives as a plain `<item>` and renders as the title alone.
 *
 * The eyebrow is deliberately neutral ("What we learned") — the card must
 * read honestly whether the user confirms completion or declines and keeps
 * practicing (learning-so-far).
 *
 * @param {string} innerContent - Content between opening and closing tags
 * @returns {HTMLElement|null} Rendered card element
 */
function renderCheckinTakeaway(innerContent) {
  // A section's items, KEEPING the `{header, subtitle}` pairs `parseItems`
  // resolves. Collapsing every item to its header here (what this closure used
  // to do) silently discarded the frontier's description.
  const items = (tag) => {
    const match = innerContent.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
    if (!match) return [];
    return parseItems(match[1])
      .map((item) => (item.header
        ? { header: item.header, subtitle: item.subtitle }
        : item.text ?? ''))
      .filter((item) => (typeof item === 'string' ? item : item.header));
  };
  // The same section flattened to plain lines — every beat but the frontier is
  // single-tier, and the lede never carries a subtitle.
  const lines = (tag) => items(tag).map((item) => (typeof item === 'string' ? item : item.header));
  const value = (tag) => {
    const match = innerContent.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
    return match ? decodeXmlEntities(match[1].trim()) : '';
  };

  const learned = lines('learned');
  const lesson = value('lesson');
  const prediction = value('prediction');
  const verdict = value('verdict');
  const title = value('title');
  const description = value('description');
  // Both or neither: a finding is named only when it carries its own name AND
  // the sentence stating it. Never branch on one alone.
  const named = Boolean(title && description);
  const sections = CHECKIN_TAKEAWAY_SECTIONS
    .map(([tag, label]) => ({ tag, label, items: items(tag) }))
    .filter((section) => section.items.length > 0);

  if (!named && learned.length === 0 && !lesson && sections.length === 0 && !prediction) return null;

  // A revised card (completePractice revision:true — the user corrected the
  // content before deciding) supersedes the prior one: gray it out. Runs on
  // live render AND replay — replay renders sequentially, so the newest card
  // ends live and every predecessor ends stale.
  markPriorProposalsStale('structured-card--checkin-takeaway', 'practice-completion-decision');

  const card = document.createElement('div');
  card.className = 'structured-card structured-card--takeaways structured-card--checkin-takeaway';

  const header = document.createElement('div');
  header.className = 'structured-card__label';
  header.textContent = CHECKIN_TAKEAWAY_HEADER;
  card.appendChild(header);

  const sectionsWrap = document.createElement('div');
  sectionsWrap.className = 'checkin-takeaway__sections';

  // 1 — The finding, on the card's own ground. It is the headline, not a
  // callout: the one thing the user is meant to leave with. A named finding
  // leads with its own name, then the sentence stating it, then the `lesson`
  // the close earned — the model's one-sentence directive — as the
  // carry-forward line. `learned` is deliberately absent there: the
  // description is its synthesis, and the finding detail page drops it on a
  // named close too. Nothing here is punctuated as a quote; every one
  // of these slots is synthesis, so quote marks would misattribute.
  const ledeParagraph = (text, modifier = '') => {
    const el = document.createElement('p');
    el.className = modifier
      ? `checkin-takeaway__lede-text ${modifier}`
      : 'checkin-takeaway__lede-text';
    el.textContent = text;
    return el;
  };

  if (named) {
    const lede = document.createElement('div');
    lede.className = 'checkin-takeaway__lede';

    // A div, not a heading: the card carries no document outline, and a
    // heading element would bring UA margins into the lede's rhythm.
    const titleEl = document.createElement('div');
    titleEl.className = 'checkin-takeaway__title';
    titleEl.textContent = title;
    lede.appendChild(titleEl);

    lede.appendChild(ledeParagraph(description));
    if (lesson) {
      lede.appendChild(ledeParagraph(lesson, 'checkin-takeaway__lede-text--lesson'));
    }
    sectionsWrap.appendChild(lede);
  } else if (learned.length > 0 || lesson) {
    // Legacy path — a close written before the finding was named. The lesson
    // leads by position; the learned lines follow. No inner label: its words
    // are the card's eyebrow now, and repeating them would print them twice.
    const lede = document.createElement('div');
    lede.className = 'checkin-takeaway__lede';

    if (lesson) {
      lede.appendChild(ledeParagraph(lesson, 'checkin-takeaway__lede-text--lesson'));
    }
    for (const item of learned) {
      lede.appendChild(ledeParagraph(item));
    }
    sectionsWrap.appendChild(lede);
  }

  // 2 — ONE plate carrying the remaining beats, fused by the shared
  // hairline-and-sublabel primitive so they read as one block with several
  // beats rather than several stacked panels.
  const plate = document.createElement('div');
  plate.className = 'sd-takeaway checkin-takeaway__section';
  let leadTaken = false;

  /**
   * Append one beat. The first beat present takes the plate's eyebrow slot;
   * every later one gets the hairline sub-label that fuses it to the plate.
   * A line may be a plain string or a `{header, subtitle}` pair (the frontier's
   * title + description) — the pair renders as two tiers in the shared
   * item-header/item-subtitle register, never collapsed to its header.
   * @param {string} label
   * @param {Array<string|{header: string, subtitle: string}>} lines
   * @param {Object} [options]
   * @param {boolean} [options.bullets=false] - Render lines as a disc list
   * @param {boolean} [options.quoted=false] - Wrap each line in curly quotes
   * @param {boolean} [options.emphasis=false] - Verdict ink
   */
  const beat = (label, lines, { bullets = false, quoted = false, emphasis = false } = {}) => {
    if (lines.length === 0) return;
    const labelEl = document.createElement('div');
    labelEl.className = leadTaken ? 'sd-takeaway__sublabel' : 'sd-takeaway__label';
    labelEl.textContent = label;
    plate.appendChild(labelEl);
    leadTaken = true;

    const list = bullets ? document.createElement('ul') : null;
    if (list) {
      list.className = 'checkin-takeaway__observed';
      plate.appendChild(list);
    }
    for (const line of lines) {
      if (typeof line !== 'string') {
        // Two tiers in the shared item register. Inside a bulleted beat they
        // share ONE <li> so the list stays well-formed; on the plate they are
        // stacked siblings.
        const host = list ? document.createElement('li') : plate;

        const headerEl = document.createElement('div');
        headerEl.className = 'structured-card__item-header';
        headerEl.textContent = line.header;
        host.appendChild(headerEl);

        const subtitleEl = document.createElement('div');
        subtitleEl.className = 'structured-card__item-subtitle';
        subtitleEl.textContent = line.subtitle;
        host.appendChild(subtitleEl);

        if (list) list.appendChild(host);
        continue;
      }
      const el = document.createElement(bullets ? 'li' : 'p');
      el.className = 'sd-takeaway__text';
      if (emphasis) el.classList.add('checkin-takeaway__verdict');
      el.textContent = quoted ? `“${line}”` : line;
      (list || plate).appendChild(el);
    }
  };

  // The prediction beat reads answer-first: the verdict, then the claim it
  // answers. Both are soft — a prediction-less experiment opens on what was
  // observed instead, never on an empty claim line. The claim is unquoted, and
  // it carries no eyebrow: every band already opens on "What we tested", so
  // the band IS this beat's heading and the claim sits under it. The beat still takes the plate's lead slot, so the
  // beats after it keep their hairline sub-labels.
  if (prediction) {
    const band = document.createElement('p');
    band.className = 'sd-takeaway__text checkin-takeaway__verdict';
    band.textContent = VERDICT_BANDS[verdict] || VERDICT_BAND_FALLBACK;
    plate.appendChild(band);
    leadTaken = true;

    const claim = document.createElement('p');
    claim.className = 'sd-takeaway__text';
    claim.textContent = prediction;
    plate.appendChild(claim);
  }

  for (const section of sections) {
    beat(section.label, section.items, { bullets: section.tag === 'evidence' });
  }

  if (plate.childElementCount > 0) sectionsWrap.appendChild(plate);
  card.appendChild(sectionsWrap);
  return card;
}

/**
 * Stale-walk helper — marks prior proposal cards and their button containers
 * as superseded when a new proposal of the same type renders.
 *
 * @param {string} cardClass - e.g. 'structured-card--practice-proposal'
 * @param {string} groupId - e.g. 'practice-proposal-decision'
 */
function markPriorProposalsStale(cardClass, groupId) {
  const root = document.getElementById('messages-list') || document;
  root.querySelectorAll(`.${cardClass}:not(.${cardClass}--stale)`)
    .forEach(el => el.classList.add(`${cardClass}--stale`));
  root.querySelectorAll(`.action-buttons[data-group-id="${groupId}"]:not(.action-buttons--stale)`)
    .forEach(el => el.classList.add('action-buttons--stale'));

  // If a prior proposal's buttons were relocated into the chat input, the transcript
  // stale-walk above can't reach them — tear them down explicitly so a new proposal
  // card (which can stream before its show_buttons) can't leave stale-practiceId
  // Accept/Reject buttons live in the input (S4).
  const input = window.app?.modules?.input;
  if (input?.getOwningButtonGroupId?.() === groupId) {
    input.hideButtons({ groupId });
    window.actionButtons?.clearActiveButtons?.();
  }
}

/**
 * Render a <practice-proposal> block — a structured proposal card for a draft
 * practice. Emitted by TreatmentStrategy / AdviceStrategy when the agent calls
 * createPractice / proposeOrientationPractice. Accept/Reject buttons are
 * rendered separately via the showButtons terminateWith action.
 *
 * Expected XML format:
 *   <practice-proposal>
 *   <id>{practiceId}</id>
 *   <detail encoding="base64">{base64 JSON snapshot}</detail>
 *   </practice-proposal>
 *
 * Snapshot shape (flat):
 *   { id, title, practiceCode, action, rationale, whatToNotice, prediction,
 *     checkInIntervalDays, type, definition: { name, category }, windowPicker? }
 *
 * `windowPicker` is written only when the backend suppresses the chip row
 * (orientation); absent means show it. It is honoured on the live card only —
 * every non-interactive path renders the prose window sentence.
 *
 * The card reads in two tiers: title + action, then the test block.
 * `prediction` (the experiment's written claim), `whatToNotice` (how you would
 * know) and `rationale` (why it matters) fuse into ONE warm plate — see
 * components/predictionBlock.js for the fusion and its null matrix. All three
 * are soft slots; with none of them, the plate is omitted entirely rather than
 * rendered as an empty callout.
 *
 * @param {string} innerContent
 * @returns {HTMLElement|null}
 */
function renderPracticeProposal(innerContent, { interactive = true } = {}) {
  const detailStr = parseDetailElement(innerContent);
  if (!detailStr) return null;

  let snapshot;
  try {
    snapshot = JSON.parse(detailStr);
  } catch {
    console.error('[structuredBlockRenderers] Failed to parse practice-proposal snapshot');
    return null;
  }

  const {
    id, title, practiceCode, action, rationale, whatToNotice, prediction,
    checkInIntervalDays, definition, windowPicker
  } = snapshot;
  if (!id || !action) return null;

  // Live half of terminus retirement (advice-unified-terminus §3.9): a rendered
  // practice proposal begins the terminus, so EVERY live Advice terminus pill
  // (artifact + correction) leaves the row — not just `build-a-practice`.
  // Unconditional (NOT gated on `interactive`) — idempotent/no-op when nothing
  // is live, so history replays are safe; durable retirement is server-side at
  // the practiceProposalOrigin persist.
  clearAllAdviceTerminusPills();

  // Static replay (interactive: false) must not run the live side effect that
  // tears down active input buttons, nor stamp the data-practice-id that live
  // accept/reject handlers match via a page-wide querySelector — a transcript
  // card must never be mistaken for the live proposal.
  if (interactive) {
    markPriorProposalsStale('structured-card--practice-proposal', 'practice-proposal-decision');
  }

  const card = document.createElement('div');
  card.className = 'structured-card structured-card--practice-proposal';
  if (interactive) {
    card.dataset.state = 'ready';
    card.dataset.practiceId = id;
  }

  const label = document.createElement('div');
  label.className = 'structured-card__label';
  label.textContent = 'Experiment To Try';
  card.appendChild(label);

  const list = document.createElement('ul');
  list.className = 'structured-card__items';

  const item = document.createElement('li');
  item.className = 'structured-card__item';

  const cardTitle = (typeof title === 'string' && title.trim())
    || definition?.name
    || practiceCode
    || 'Experiment';
  const rationaleText = typeof rationale === 'string' ? rationale.trim() : '';
  const whatToNoticeText = typeof whatToNotice === 'string' ? whatToNotice.trim() : '';
  const predictionText = typeof prediction === 'string' ? prediction.trim() : '';

  const headerEl = document.createElement('div');
  headerEl.className = 'structured-card__item-header';
  headerEl.textContent = cardTitle;
  item.appendChild(headerEl);

  if (action) {
    const subtitleEl = document.createElement('div');
    subtitleEl.className = 'structured-card__item-subtitle';
    subtitleEl.textContent = action;
    item.appendChild(subtitleEl);
  }

  // The test — the claim, how you would know, and what rides on the answer —
  // is the point of the experiment, so it renders open and it renders as ONE
  // plate. `whatToNotice` and `rationale` live INSIDE it as later beats: the
  // measurement spec and the stakes belong to the prediction, not beside it.
  const testBlock = buildPredictionBlock(predictionText, {
    notice: whatToNoticeText,
    rationale: rationaleText
  });
  if (testBlock) item.appendChild(testBlock);

  list.appendChild(item);
  card.appendChild(list);

  const windowRow = buildWindowPicker(snapshot, { interactive });
  if (windowRow) card.appendChild(windowRow);

  return withHelpHint(card, 'practices', { interactive });
}

/**
 * Build the proposal card's window row.
 *
 * On the LIVE card (and only there, and only when the backend has not
 * suppressed it) this is a row of selectable chips: the user — not the model —
 * settles how long the experiment runs. Clicking a chip moves `aria-pressed`
 * and nothing else; no request is made and no state lives outside the card.
 * `ActionRouter.accept_practice` reads the pressed chip's `data-days` and sends
 * it with the accept.
 *
 * Every other path — transcript replay, session detail, and the suppressed
 * orientation card — renders today's prose sentence instead. The stored
 * assistant message is a record of what was PROPOSED; the live card is where
 * the choice is made.
 *
 * @param {Object} snapshot - The full practice-proposal snapshot
 * @param {Object} [opts]
 * @param {boolean} [opts.interactive=true] - False on replay/detail renders
 * @returns {HTMLElement|null}
 */
function buildWindowPicker(snapshot, { interactive = true } = {}) {
  const intervalDays = snapshot?.checkInIntervalDays;
  if (!interactive || snapshot?.windowPicker === false) {
    return buildExperimentWindowFooter(intervalDays);
  }

  const row = document.createElement('div');
  row.className = 'structured-card__window';

  const label = document.createElement('span');
  label.className = 'structured-card__window-label';
  label.textContent = 'Follow up after:';
  row.appendChild(label);

  const selected = isValidWindowInterval(intervalDays) ? intervalDays : null;
  for (const option of deriveWindowOptions(intervalDays)) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'structured-card__window-chip';
    chip.dataset.days = String(option);
    chip.textContent = formatWindowOption(option);
    chip.setAttribute('aria-pressed', option === selected ? 'true' : 'false');
    chip.addEventListener('click', () => {
      if (chip.disabled) return;
      for (const sibling of row.querySelectorAll('button')) {
        sibling.setAttribute('aria-pressed', sibling === chip ? 'true' : 'false');
      }
    });
    row.appendChild(chip);
  }

  return row;
}

/**
 * Build the proposal card's window footer — the one-line statement of the
 * observation window the experiment runs for.
 *
 * Omitted when the interval is unknown (legacy rows carry a null
 * `checkInIntervalDays`); the card must never print "null days".
 *
 * @param {number|null|undefined} intervalDays
 * @returns {HTMLElement|null}
 */
function buildExperimentWindowFooter(intervalDays) {
  if (!Number.isFinite(intervalDays) || intervalDays < 1) return null;
  const days = Math.round(intervalDays);
  const footer = document.createElement('div');
  footer.className = 'structured-card__window';
  footer.textContent = days === 1
    ? 'One day of watching, then you report back.'
    : `${days} days of watching, then you report back.`;
  return footer;
}

/**
 * Render a <memo-proposal> block — a "Note to {partner}" card holding a draft
 * partner memo. Emitted by AdviceStrategy when the agent calls composeMemo for a
 * coupled user. Send / Warmer / Shorter / Discard buttons render separately via
 * the showButtons terminateWith action (group `partner-memo-review`).
 *
 * Expected XML format (base64 snapshot; prose lives inside the snapshot so it can
 * never break the tag parser — only <id> is raw):
 *   <memo-proposal>
 *   <id>{memoId}</id>
 *   <detail encoding="base64">{base64 JSON snapshot}</detail>
 *   </memo-proposal>
 *
 * Snapshot shape (flat): { id, content, recipientName }
 *   id            = memoId (== <id>)
 *   content       = the agent-authored memo prose (may be multi-paragraph)
 *   recipientName = partner display name for the "Note to {name}" header
 *
 * @param {string} innerContent
 * @param {{interactive?: boolean}} [opts]
 * @returns {HTMLElement|null}
 */
function renderMemoProposal(innerContent, { interactive = true } = {}) {
  const detailStr = parseDetailElement(innerContent);
  if (!detailStr) return null;

  let snapshot;
  try {
    snapshot = JSON.parse(detailStr);
  } catch {
    console.error('[structuredBlockRenderers] Failed to parse memo-proposal snapshot');
    return null;
  }

  const { id, title, content, recipientName } = snapshot;
  const memoText = typeof content === 'string' ? content.trim() : '';
  if (!id || !memoText) return null;

  // Live half of terminus retirement (advice-unified-terminus §3.9): a rendered
  // memo card begins the terminus, so EVERY live Advice terminus pill (artifact +
  // correction) leaves the row — not just the two memo pills. Unconditional (NOT
  // gated on `interactive`) — idempotent/no-op when nothing is live, so history
  // replays are safe; durable retirement is server-side at the
  // memoComposedInSession stamp.
  clearAllAdviceTerminusPills();

  // Static replay (interactive: false) must not run the live stale-walk (which
  // tears down active input buttons) nor stamp the data-memo-id that the live
  // send/discard handlers match via a page-wide querySelector — a transcript
  // card must never be mistaken for the live draft.
  if (interactive) {
    markPriorProposalsStale('structured-card--memo-proposal', 'partner-memo-review');
  }

  const card = document.createElement('div');
  card.className = 'structured-card structured-card--memo-proposal';
  if (interactive) {
    card.dataset.state = 'ready';
    card.dataset.memoId = id;
  }

  const label = document.createElement('div');
  label.className = 'structured-card__label';
  const recipient = typeof recipientName === 'string' ? recipientName.trim() : '';
  label.textContent = recipient ? `Note to ${recipient}` : 'Note to your partner';
  card.appendChild(label);

  // Note title (agent-authored inbox line; absent on pre-title snapshots).
  const noteTitle = typeof title === 'string' ? title.trim() : '';
  if (noteTitle) {
    const heading = document.createElement('h4');
    heading.className = 'structured-card__title';
    heading.textContent = noteTitle;
    card.appendChild(heading);
  }

  // Render the memo prose as one <p> per paragraph (blank-line separated).
  // textContent keeps it XSS-safe — the memo is model-authored free text.
  const paragraphs = memoText.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  for (const para of paragraphs) {
    const p = document.createElement('p');
    p.className = 'structured-card__text structured-card__memo-text';
    p.textContent = para;
    card.appendChild(p);
  }

  // MUST return the wrapper, not `card`. withHelpHint MOVES the card into a new
  // detached wrapper — `withHelpHint(card, …); return card;` would still render
  // the card (appendChild re-parents it) but silently drop the hint, having
  // ALREADY recorded an exposure for something the user never saw.
  return withHelpHint(card, 'notes', { interactive });
}

/**
 * Render a <practice-reference> block — the compact, clickable card pinned at
 * the top of the first assistant message in a TREATMENT/checkin session.
 *
 * Server-generated and prepended via SonnetGenerator's prependContent path.
 * Clicking the card opens the in-chat right-side panel with the full practice
 * detail (rendered by `renderPracticeReferencePanel` from the embedded
 * base64 snapshot).
 *
 * Expected XML format:
 *   <practice-reference>
 *   <label>Experiment — accepted May 6</label>
 *   <title>Notice the bid window</title>
 *   <description>Once a day, pause when Mark makes a small bid...</description>
 *   <detail encoding="base64">...</detail>
 *   </practice-reference>
 *
 * Defensive fallback: if `<detail>` is missing or base64 decode fails, the
 * card still renders (label + title + description) but is non-clickable.
 *
 * @param {string} innerContent
 * @returns {HTMLElement|null}
 */
function renderPracticeReference(innerContent, { interactive = true } = {}) {
  const title = parseElement(innerContent, 'title');
  const description = parseElement(innerContent, 'description');

  if (!title) return null;

  let snapshot = null;
  const detailStr = parseDetailElement(innerContent);
  if (detailStr) {
    try {
      snapshot = JSON.parse(detailStr);
    } catch (err) {
      console.warn('[structuredBlockRenderers] practice-reference: failed to parse snapshot; rendering non-clickable card', err);
    }
  }

  // Static replay (interactive: false) renders a plain, non-clickable artifact:
  // no clickable class, no button role/tabindex, no chevron, no listeners.
  const isClickable = !!snapshot && interactive;
  const subtitle = buildPracticeReferenceSubtitle(snapshot);
  const card = buildArtifactCard({
    cardClass: isClickable
      ? 'artifact artifact--practice-reference artifact--clickable'
      : 'artifact artifact--practice-reference',
    iconHref: '#icon-practices',
    title,
    description,
    subtitle,
    showChevron: interactive
  });
  if (interactive) {
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
  }

  if (isClickable) {
    const openPanel = () => {
      const panel = renderPracticeReferencePanel(snapshot);
      eventBus.emit('right-panel:open', {
        title: 'Experiment',
        content: panel,
        contentType: 'practice-reference'
      });
    };
    card.addEventListener('click', openPanel);
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openPanel();
      }
    });
  }

  return card;
}

/**
 * Build the practice-reference artifact subtitle string:
 *   "Started May 24 · 2 progress entries"
 *
 * @param {Object|null} snapshot
 * @returns {string}
 */
function buildPracticeReferenceSubtitle(snapshot) {
  if (!snapshot) return '';
  const parts = [];

  const startedIso = snapshot.activatedAt || snapshot.createdAt;
  if (startedIso) {
    const d = new Date(startedIso);
    if (!Number.isNaN(d.getTime())) {
      const formatted = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      parts.push(`Started ${formatted}`);
    }
  }

  const count = Array.isArray(snapshot.logs) ? snapshot.logs.length : 0;
  parts.push(`${count} progress ${count === 1 ? 'entry' : 'entries'}`);

  return parts.join(' · ');
}

/**
 * Render a <memo-reference> block — the pinned "Note from {partner}" card at the top
 * of a receiver's "discuss this note" (memo_entry) ADVICE session. Sibling of
 * <memo-proposal> (same base64-snapshot discipline). The prose is clamped to two
 * lines with an ellipsis in the chat; clicking (or tapping) the card opens the
 * full note — same body markup as /partner-notes — in the right panel via
 * `renderNoteDetailBody`. No stale-walk, no data-memo-id, no accept/decline
 * buttons — it just anchors and expands the note being discussed.
 *
 * Expected XML format:
 *   <memo-reference>
 *   <id>{memoId}</id>
 *   <detail encoding="base64">{base64 JSON snapshot}</detail>
 *   </memo-reference>
 *
 * Snapshot shape (flat): { id, title, content, senderName, sentAt }
 *
 * @param {string} innerContent
 * @param {{interactive?: boolean}} [options] - interactive: false (static replay)
 *   renders the plain, non-clickable variant with no chevron/listeners.
 * @returns {HTMLElement|null}
 */
function renderMemoReference(innerContent, { interactive = true } = {}) {
  const detailStr = parseDetailElement(innerContent);
  if (!detailStr) return null;

  let snapshot;
  try {
    snapshot = JSON.parse(detailStr);
  } catch {
    console.error('[structuredBlockRenderers] Failed to parse memo-reference snapshot');
    return null;
  }

  const { id, title, content, senderName, sentAt } = snapshot;
  const memoText = typeof content === 'string' ? content.trim() : '';
  if (!id || !memoText) return null;

  const sender = typeof senderName === 'string' ? senderName.trim() : '';
  const noteTitle = typeof title === 'string' ? title.trim() : '';

  const card = document.createElement('div');
  card.className = interactive
    ? 'structured-card structured-card--memo-reference structured-card--clickable'
    : 'structured-card structured-card--memo-reference';
  if (interactive) {
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
  }

  // Label row (colored label + optional "Read full note" chevron)
  const labelRow = document.createElement('div');
  labelRow.className = 'structured-card__label-row';

  const label = document.createElement('div');
  label.className = 'structured-card__label';
  label.textContent = sender ? `Note from ${sender}` : 'Note from your partner';
  labelRow.appendChild(label);

  if (interactive) {
    const learnMore = document.createElement('span');
    learnMore.className = 'structured-card__learn-more';
    learnMore.innerHTML = `
      <span class="structured-card__learn-more-text">Read full note</span>
      <svg class="structured-card__learn-more-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5"
          stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    labelRow.appendChild(learnMore);
  }

  card.appendChild(labelRow);

  // Date meta sits above the title, in the same "Received · July 5, 2026" shape
  // the right-panel detail view uses (partnerNotesDrillHelper). The format is
  // duplicated rather than imported so this renderer keeps the drill helper out
  // of its static module graph (it stays a dynamic import, below).
  if (sentAt) {
    const when = new Date(sentAt);
    if (!Number.isNaN(when.getTime())) {
      const meta = document.createElement('div');
      meta.className = 'structured-card__meta structured-card__memo-meta';
      const date = when.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      meta.textContent = `Received · ${date}`;
      card.appendChild(meta);
    }
  }

  // Note title (agent-authored inbox line; absent on pre-title snapshots).
  if (noteTitle) {
    const heading = document.createElement('h4');
    heading.className = 'structured-card__title';
    heading.textContent = noteTitle;
    card.appendChild(heading);
  }

  // Two-line clamped preview — paragraph breaks flatten to a single space so
  // the CSS line-clamp truncates cleanly; the full paragraph structure only
  // renders in the expanded right-panel view (via renderNoteDetailBody).
  const preview = document.createElement('p');
  preview.className = 'structured-card__text structured-card__memo-text structured-card__memo-preview';
  preview.textContent = memoText.replace(/\s+/g, ' ').trim();
  card.appendChild(preview);

  if (interactive) {
    // Dynamic import: partnerNotesDrillHelper.js is only needed once the card
    // is actually opened, keeping it out of every other renderer's module graph.
    const openFullNote = async () => {
      const { renderNoteDetailBody } = await import('../components/partnerNotesDrillHelper.js');
      const panelContent = document.createElement('div');
      panelContent.className = 'note-detail';
      panelContent.innerHTML = renderNoteDetailBody(
        { title: noteTitle, content: memoText, sentAt },
        { list: 'received', partnerName: sender || 'your partner', ownName: '' }
      );

      eventBus.emit('right-panel:open', {
        title: '',
        content: panelContent,
        contentType: 'memo-reference'
      });
    };

    card.addEventListener('click', openFullNote);
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openFullNote();
      }
    });
  }

  return card;
}

/**
 * Registry mapping tag names to renderer functions.
 * @type {Map<string, function(string): HTMLElement|null>}
 */
export const structuredBlockRenderers = new Map([
  ['l2-closing-summary', renderL2ClosingSummary],
  ['reflection', renderReflection],
  ['insight', renderInsight],
  ['topic-understanding', renderTopicUnderstanding],
  ['brain-reveal', renderBrainReveal],
  ['intermediary-observations', renderIntermediaryObservations],
  ['exploration', renderExploration],
  ['common-issues', renderCommonIssues],
  ['advice-topics', renderAdviceTopics],
  ['experiment-directions', renderExperimentDirections],
  ['advice-takeaway', renderAdviceTakeaway],
  ['checkin-takeaway', renderCheckinTakeaway],
  ['practice-proposal', renderPracticeProposal],
  ['memo-proposal', renderMemoProposal],
  ['memo-reference', renderMemoReference],
  ['practice-reference', renderPracticeReference],
  ['guide-announcement', renderGuideAnnouncement],
  ['sharing-invitation-card', renderSharingInvitationCard],
]);

/**
 * Self-timed structured blocks: cards that keep animating after their own
 * fade-in completes. Maps tag name → a getter for how much longer
 * BlockStreamBuffer must hold every later block before it appears, so text
 * doesn't surface while the card still reads "Updating memory". Blocks not
 * listed here are assumed done as soon as their fade-in finishes.
 */
export const structuredBlockHoldMs = new Map([
  ['topic-understanding', understandingCardHoldMs],
]);

/**
 * Append a prose segment rendered as sanitized markdown. No-op for blank text.
 * @param {DocumentFragment|HTMLElement} parent
 * @param {string} text
 */
function appendProse(parent, text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return;
  const div = document.createElement('div');
  div.className = 'message__text';
  div.innerHTML = renderMarkdown(trimmed);
  parent.appendChild(div);
}

/**
 * Flatten an `<item>`-bearing structured block into markdown bullets.
 * `<header>`/`<subtitle>` pairs render as "- **header** — subtitle".
 * @param {string} match - Full matched block source
 * @returns {string[]} Bullet lines (empty when the block has no items)
 */
/**
 * Markdown form of an inline suggestion-menu block (<advice-topics>,
 * <experiment-directions>): bold label + bullet items, or '' when empty.
 *
 * @param {string} match - The full block including its tags
 * @param {string} defaultTitle - Label used when the block carries no <title>
 * @returns {string}
 */
function suggestionMenuToMarkdown(match, defaultTitle) {
  const title = parseElement(match, 'title') || defaultTitle;
  const bullets = itemsToBullets(match);
  return bullets.length ? `\n**${title}**\n${bullets.join('\n')}\n` : '';
}

function itemsToBullets(match) {
  return [...match.matchAll(/<item>([\s\S]*?)<\/item>/g)].map(m => {
    const inner = m[1].trim();
    const headerMatch = inner.match(/<header>([\s\S]*?)<\/header>/);
    const subtitleMatch = inner.match(/<subtitle>([\s\S]*?)<\/subtitle>/);
    if (headerMatch && subtitleMatch) {
      return `- **${headerMatch[1].trim()}** — ${subtitleMatch[1].trim()}`;
    }
    return `- ${inner}`;
  });
}

/**
 * Convert raw assistant content (prose + structured XML blocks) into the
 * readable markdown used by the copy button.
 *
 * Single source of truth for copy text: the live streaming path and the
 * all-at-once path (poll fallback + history replay) must produce byte-identical
 * output, or copying the same message would return prose live and raw XML after
 * a reload.
 *
 * @param {string} raw - Raw stored/streamed assistant content
 * @returns {string} Readable markdown with structured blocks flattened
 */
export function toCopyMarkdown(raw) {
  if (!raw || typeof raw !== 'string') return '';

  const flattenBlock = (match) => {
    const bullets = itemsToBullets(match);
    return bullets.length ? '\n' + bullets.join('\n') + '\n' : '';
  };

  // Decode the base64/raw `<detail>` snapshot shared by the artifact cards
  // (practice-proposal, memo-proposal, memo-reference). Returns null when the
  // detail is absent or unparseable — the flattener then drops the card.
  const parseSnapshot = (match) => {
    const detailStr = parseDetailElement(match);
    if (!detailStr) return null;
    try {
      return JSON.parse(detailStr);
    } catch {
      return null;
    }
  };

  return raw
    .replace(/<l2-closing-summary>[\s\S]*?<\/l2-closing-summary>/g, flattenBlock)
    .replace(/<advice-takeaway>[\s\S]*?<\/advice-takeaway>/g, flattenBlock)
    // <checkin-takeaway> — sectioned card; a bare itemsToBullets would lose
    // the fixed section labels, so flatten each section under its heading.
    .replace(/<checkin-takeaway>[\s\S]*?<\/checkin-takeaway>/g, (match) => {
      const section = (tag) => {
        const m = match.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
        return m ? itemsToBullets(m[1]) : [];
      };
      const single = (tag) => {
        const m = match.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
        return m ? decodeXmlEntities(m[1].trim()) : '';
      };

      let out = '';
      // The lede, in the card's DOM order and under the block's own
      // `**What we learned**` heading — no inner sub-heading, which would
      // print the same words twice. A named finding (title AND description,
      // never one alone) leads with its name, then the sentence stating it,
      // then the lesson; `learned` stays off it, as on the card. Otherwise the
      // legacy lede: the lesson line above the learned bullets.
      const learned = section('learned');
      const lesson = single('lesson');
      const findingTitle = single('title');
      const findingDescription = single('description');
      const lede = (findingTitle && findingDescription)
        ? [findingTitle, findingDescription, lesson]
        : [lesson, ...learned];
      const ledeLines = lede.filter(Boolean);
      if (ledeLines.length) out += `\n${ledeLines.join('\n')}\n`;

      // Mirrors the card: the band is the beat's own heading, so it takes the
      // bold slot the eyebrow used to hold rather than stacking under it.
      const prediction = single('prediction');
      if (prediction) {
        const verdict = VERDICT_BANDS[single('verdict')] || VERDICT_BAND_FALLBACK;
        out += `\n**${verdict}**\n\n> ${prediction}\n`;
      }

      for (const [tag, label] of CHECKIN_TAKEAWAY_SECTIONS) {
        const bullets = section(tag);
        if (bullets.length) out += `\n**${label}**\n${bullets.join('\n')}\n`;
      }
      return out ? `\n**${CHECKIN_TAKEAWAY_HEADER}**\n${out}` : '';
    })
    .replace(/<common-issues>[\s\S]*?<\/common-issues>/g, flattenBlock)
    .replace(/<guide-announcement>[\s\S]*?<\/guide-announcement>/g, flattenGuideAnnouncement)
    .replace(/<sharing-invitation-card>[\s\S]*?<\/sharing-invitation-card>/g, flattenSharingInvitationCard)
    .replace(/<reflection>\s*<text>([\s\S]*?)<\/text>\s*<\/reflection>/g, '> $1')
    .replace(/<insight>[\s\S]*?<\/insight>/g, (match) => {
      const labelMatch = match.match(/<label>([\s\S]*?)<\/label>/);
      const label = labelMatch ? labelMatch[1].trim() : 'Insight';
      const bullets = itemsToBullets(match);
      return bullets.length ? `\n**${label}**\n${bullets.join('\n')}\n` : '';
    })
    .replace(/<topic-understanding>[\s\S]*?<\/topic-understanding>/g, (match) => {
      // History: the live animated card isn't re-rendered on reload — show a
      // static, non-interactive summary line instead.
      const label = (match.match(/<label>([\s\S]*?)<\/label>/) || [])[1]?.trim();
      if (!label) return '';
      // A no-op close renders the card's "already saved" state, so the copied
      // line must not claim an update. The L1 flash (tier 0) also carries
      // changed=false — its tier is persisted before the payload is computed —
      // and IS a real write, so it keeps the update line.
      const changed = (match.match(/<changed>([\s\S]*?)<\/changed>/) || [])[1]?.trim();
      const tier = (match.match(/<tier>([\s\S]*?)<\/tier>/) || [])[1]?.trim();
      if (changed === 'false' && tier !== '0') {
        return `\n**Checked against saved understanding** — no change\n`;
      }
      const progRaw = (match.match(/<progress>([\s\S]*?)<\/progress>/) || [])[1];
      const pct = progRaw != null ? ` — ${Math.round(parseFloat(progRaw) * 100)}%` : '';
      return `\n**Understanding updated:** ${label}${pct}\n`;
    })
    // <intermediary-observations> — two hardcoded sections ("What I'm Noticing"
    // takeaways + optional "What I'd Still Like to Explore"). Runs BEFORE the
    // standalone <exploration> flattener so it consumes its own nested block.
    .replace(/<intermediary-observations>[\s\S]*?<\/intermediary-observations>/g, (match) => {
      const takeawaysMatch = match.match(/<takeaways>([\s\S]*?)<\/takeaways>/);
      const takeawayBullets = takeawaysMatch ? itemsToBullets(takeawaysMatch[1]) : [];
      if (takeawayBullets.length === 0) return '';
      const explorationMatch = match.match(/<exploration>([\s\S]*?)<\/exploration>/);
      const explorationBullets = explorationMatch ? itemsToBullets(explorationMatch[1]) : [];
      let out = `\n**What I'm Noticing**\n${takeawayBullets.join('\n')}\n`;
      if (explorationBullets.length) {
        out += `\n**What I'd Still Like to Explore**\n${explorationBullets.join('\n')}\n`;
      }
      return out;
    })
    // Standalone <exploration> sibling card (clinical path after an <insight>).
    .replace(/<exploration>[\s\S]*?<\/exploration>/g, (match) => {
      const bullets = itemsToBullets(match);
      return bullets.length
        ? `\n**What I'd Still Like to Explore**\n${bullets.join('\n')}\n`
        : '';
    })
    // <advice-topics> — Foundation → Advice topic menu (label + items).
    .replace(/<advice-topics>[\s\S]*?<\/advice-topics>/g, (match) =>
      suggestionMenuToMarkdown(match, 'Some topics we could explore'))
    // <experiment-directions> — pre-selected experiment directions (label + items).
    .replace(/<experiment-directions>[\s\S]*?<\/experiment-directions>/g, (match) =>
      suggestionMenuToMarkdown(match, 'Experiments we could try'))
    // <practice-proposal> — "Experiment To Try" card (base64 snapshot).
    .replace(/<practice-proposal>[\s\S]*?<\/practice-proposal>/g, (match) => {
      const snap = parseSnapshot(match);
      if (!snap || !snap.action) return '';
      const title = (typeof snap.title === 'string' && snap.title.trim())
        || snap.definition?.name || snap.practiceCode || 'Experiment';
      let head = `- **${title}**`;
      if (typeof snap.action === 'string' && snap.action.trim()) {
        head += ` — ${snap.action.trim()}`;
      }
      const parts = ['\n**Experiment To Try**', head];
      if (typeof snap.rationale === 'string' && snap.rationale.trim()) {
        parts.push(`\n**Why This Matters**\n${snap.rationale.trim()}`);
      }
      if (typeof snap.whatToNotice === 'string' && snap.whatToNotice.trim()) {
        parts.push(`\n**What To Notice**\n${snap.whatToNotice.trim()}`);
      }
      return parts.join('\n') + '\n';
    })
    // <memo-proposal> — "Note to {partner}" draft (base64 snapshot).
    .replace(/<memo-proposal>[\s\S]*?<\/memo-proposal>/g, (match) => {
      const snap = parseSnapshot(match);
      const content = snap && typeof snap.content === 'string' ? snap.content.trim() : '';
      if (!content) return '';
      const recipient = typeof snap.recipientName === 'string' ? snap.recipientName.trim() : '';
      const parts = [`\n**${recipient ? `Note to ${recipient}` : 'Note to your partner'}**`];
      const noteTitle = typeof snap.title === 'string' ? snap.title.trim() : '';
      if (noteTitle) parts.push(`**${noteTitle}**`);
      parts.push(content);
      return parts.join('\n\n') + '\n';
    })
    // <memo-reference> — "Note from {partner}" pinned card (base64 snapshot).
    .replace(/<memo-reference>[\s\S]*?<\/memo-reference>/g, (match) => {
      const snap = parseSnapshot(match);
      const content = snap && typeof snap.content === 'string' ? snap.content.trim() : '';
      if (!content) return '';
      const sender = typeof snap.senderName === 'string' ? snap.senderName.trim() : '';
      const parts = [`\n**${sender ? `Note from ${sender}` : 'Note from your partner'}**`];
      const noteTitle = typeof snap.title === 'string' ? snap.title.trim() : '';
      if (noteTitle) parts.push(`**${noteTitle}**`);
      parts.push(content);
      return parts.join('\n\n') + '\n';
    })
    // <practice-reference> — pinned "Experiment" card (label + title + desc).
    .replace(/<practice-reference>[\s\S]*?<\/practice-reference>/g, (match) => {
      const title = parseElement(match, 'title');
      if (!title) return '';
      const label = parseElement(match, 'label');
      const description = parseElement(match, 'description');
      const parts = [`\n**${label || 'Experiment'}**`, `**${title}**`];
      if (description) parts.push(description);
      return parts.join('\n') + '\n';
    })
    // <focus-area-reference> — LEGACY (retired 2026-07) but persists in history.
    .replace(/<focus-area-reference>[\s\S]*?<\/focus-area-reference>/g, (match) => {
      const title = parseElement(match, 'title');
      if (!title) return '';
      const description = parseElement(match, 'description');
      return description ? `\n**${title}**\n${description}\n` : `\n**${title}**\n`;
    })
    .replace(/<([a-z][a-z0-9-]*?)(\s[^>]*)?>[\s\S]*?<\/\1>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Client-only lifecycle cards that must never be re-rendered when replaying
 * stored history: the topic-understanding card animates, and brain-reveal
 * fetches *current* brain progress, which would misrepresent an old message.
 * Shared by both replay surfaces (live resume + read-only transcript).
 * @type {Set<string>}
 */
export const REPLAY_SKIP_TAGS = new Set(['topic-understanding', 'brain-reveal']);

/**
 * Walk `content`, rendering every embedded structured block as a card
 * interleaved with the surrounding prose. This is the single shared parser
 * for every all-at-once render path (transcript replay + chat poll/reload),
 * so they all parse structured blocks identically — anywhere in the message,
 * not leading-only (INV-1). The live SSE path uses BlockStreamBuffer instead.
 *
 * A block whose registered renderer returns null or throws is dropped (rather
 * than leaking raw XML / base64 as gibberish). A truly-unknown tag never
 * matches the registered-tag regex and is rendered as sanitized prose.
 *
 * @param {string} content - Raw stored/committed assistant content
 * @param {{interactive?: boolean, skip?: Set<string>|null}} [opts]
 *   `interactive` is threaded to each block renderer (true = live cards with
 *   listeners/data attributes; false = static, non-interactive cards). `skip`
 *   is an optional set of tag names to omit entirely (e.g. lifecycle cards).
 * @returns {DocumentFragment}
 */
export function renderInterleavedBlocks(content, { interactive = true, skip = null } = {}) {
  const frag = document.createDocumentFragment();
  if (!content || typeof content !== 'string') return frag;

  const tags = [...structuredBlockRenderers.keys()];
  if (tags.length === 0) {
    appendProse(frag, content);
    return frag;
  }
  const openTag = new RegExp(`<(${tags.join('|')})>`);

  let rest = content;
  while (rest && rest.length) {
    const open = rest.match(openTag);
    if (!open) {
      appendProse(frag, rest);
      break;
    }

    // Prose preceding the block.
    if (open.index > 0) appendProse(frag, rest.slice(0, open.index));

    const tag = open[1];
    const closeTag = `</${tag}>`;
    const closeIdx = rest.indexOf(closeTag, open.index + open[0].length);
    if (closeIdx === -1) {
      // Unclosed tag — render the remainder as prose and stop.
      appendProse(frag, rest.slice(open.index));
      break;
    }

    const inner = rest.slice(open.index + open[0].length, closeIdx);
    if (!skip || !skip.has(tag)) {
      try {
        const el = structuredBlockRenderers.get(tag)(inner, { interactive });
        if (el) frag.appendChild(el);
        else console.warn(`[renderInterleavedBlocks] <${tag}> renderer returned null — block omitted`);
      } catch (err) {
        console.warn(`[renderInterleavedBlocks] failed to render <${tag}> — block omitted`, err);
      }
    }

    rest = rest.slice(closeIdx + closeTag.length);
  }

  return frag;
}
